import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  LinearProgress,
  Fade,
  Grow
} from "@mui/material";
import { useTranslation } from "react-i18next";
import {
  Home as HomeIcon,
  Laptop as LaptopIcon,
  Book as BookIcon,
  Group as UsersIcon,
  Launch as ExternalLinkIcon,
  Public as GlobeIcon,
  Logout as SignOutIcon
} from "@mui/icons-material";

import { useFileStore } from "../../app/zustandStore";
import { useFileQuery } from "../../app/apiHooks";
import { bffAuthService } from "../../services/BffAuthService";

const Portal = () => {
  const [loading, setLoading] = useState(true);
  const [hoveredCard, setHoveredCard] = useState<string | null>(null);
  
  const { t } = useTranslation();
  const isAuthenticated = bffAuthService.isAuthenticated;
  const currentFile = useFileStore((state) => state.currentFile);
  const { data: fileData, isLoading, error } = useFileQuery();

  useEffect(() => {
    if (isAuthenticated) {
      if (currentFile || fileData || !isLoading || error) {
        setLoading(false);
      }
    }
  }, [isAuthenticated, isLoading, fileData, error, currentFile]);

  const handleLogout = async () => {
    await bffAuthService.logout();
  };

  const handleSiteClick = (siteName: string, siteUrl: string) => {
    console.log(`用户点击了: ${siteName} (${siteUrl})`);
    // 可以在这里添加统计代码
  };

  if (loading) {
    return (
      <Box sx={{ width: '100%', p: 3 }}>
        <LinearProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ 
      minHeight: '100vh',
      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
      fontFamily: 'Poppins, sans-serif',
      position: 'relative',
      overflow: 'hidden'
    }}>
      {/* 背景动画 */}
      <Box sx={{
        position: 'fixed',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        zIndex: -1,
        overflow: 'hidden'
      }}>
        <Box sx={{
          position: 'absolute',
          borderRadius: '50%',
          filter: 'blur(40px)',
          opacity: 0.6,
          width: '300px',
          height: '300px',
          backgroundColor: 'rgba(26, 41, 128, 0.4)',
          top: '-100px',
          left: '-100px',
          animation: 'float 15s infinite ease-in-out'
        }} />
        <Box sx={{
          position: 'absolute',
          borderRadius: '50%',
          filter: 'blur(40px)',
          opacity: 0.6,
          width: '250px',
          height: '250px',
          backgroundColor: 'rgba(38, 208, 206, 0.4)',
          bottom: '-100px',
          right: '-100px',
          animation: 'float 20s infinite ease-in-out reverse'
        }} />
        <Box sx={{
          position: 'absolute',
          borderRadius: '50%',
          filter: 'blur(40px)',
          opacity: 0.6,
          width: '200px',
          height: '200px',
          backgroundColor: 'rgba(255, 255, 255, 0.2)',
          top: '50%',
          left: '30%',
          animation: 'float 25s infinite ease-in-out'
        }} />
        <Box sx={{
          position: 'absolute',
          borderRadius: '50%',
          filter: 'blur(40px)',
          opacity: 0.6,
          width: '150px',
          height: '150px',
          backgroundColor: 'rgba(26, 41, 128, 0.3)',
          top: '30%',
          right: '20%',
          animation: 'float 18s infinite ease-in-out reverse'
        }} />
      </Box>

      <Box sx={{
        maxWidth: '1200px',
        margin: '0 auto',
        padding: '2rem',
        position: 'relative',
        zIndex: 1
      }}>
        {/* 头部 */}
        <Box sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '3rem',
          background: 'rgba(255, 255, 255, 0.95)',
          borderRadius: '15px',
          padding: '1.5rem 2rem',
          boxShadow: '0 10px 30px rgba(0, 0, 0, 0.1)',
          backdropFilter: 'blur(10px)'
        }}>
          <Box>
            <Typography variant="h4" sx={{
              fontWeight: 600,
              color: '#2d3748',
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem',
              marginBottom: '0.5rem'
            }}>
              <GlobeIcon sx={{ color: '#1a2980' }} />
              欢迎访问 CCDTN
            </Typography>
            <Typography variant="h6" sx={{
              color: '#718096',
              fontWeight: 400
            }}>
              请选择您要访问的网站
            </Typography>
          </Box>
          
          <Box sx={{
            display: 'flex',
            alignItems: 'center',
            gap: '1rem'
          }}>
            <Box sx={{
              display: 'flex',
              alignItems: 'center',
              gap: '0.75rem',
              padding: '0.5rem 1rem',
              background: 'rgba(26, 41, 128, 0.1)',
              borderRadius: '25px',
              border: '1px solid rgba(26, 41, 128, 0.2)'
            }}>
              <Box
                component="img"
                src="/logo_ccdtn.png"
                alt="用户头像"
                sx={{
                  width: '40px',
                  height: '40px',
                  borderRadius: '50%',
                  border: '2px solid #1a2980',
                  transition: 'all 0.3s ease'
                }}
                onError={(e) => {
                  e.currentTarget.src = '/logo_ccdtn.png';
                }}
              />
              <Typography sx={{
                fontWeight: 500,
                color: '#2d3748'
              }}>
                {currentFile?.nickName || fileData?.nickName || (fileData as any)?.data?.nickName || currentFile?.username || fileData?.username || (fileData as any)?.data?.username || '用户'}
              </Typography>
            </Box>
            
            <Box
              component="button"
              onClick={handleLogout}
              sx={{
                background: 'linear-gradient(135deg, #1a2980, #26d0ce)',
                color: 'white',
                border: 'none',
                padding: '0.75rem 1.5rem',
                borderRadius: '8px',
                fontWeight: 600,
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem',
                transition: 'all 0.3s ease',
                '&:hover': {
                  transform: 'translateY(-2px)',
                  boxShadow: '0 5px 15px rgba(26, 41, 128, 0.3)'
                }
              }}
            >
              <SignOutIcon sx={{ fontSize: '1rem' }} />
              退出登录
            </Box>
          </Box>
        </Box>

        {/* 网站网格 */}
        <Box sx={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
          gap: '2rem',
          marginBottom: '3rem'
        }}>
          {[
            {
              name: 'Wise Memo 主站',
              description: '访问 Wise Memo 主网站，享受完整的学习体验',
              url: 'https://www.wisememo.com',
              icon: <HomeIcon sx={{ fontSize: '2rem', color: 'white' }} />,
              color: 'linear-gradient(135deg, #1a2980, #26d0ce)'
            },
            {
              name: 'Wise Memo Web版',
              description: 'Web版本的 WiseMemo，随时随地访问学习资源',
              url: 'https://web.wisememo.com',
              icon: <LaptopIcon sx={{ fontSize: '2rem', color: 'white' }} />,
              color: 'linear-gradient(135deg, #667eea, #764ba2)'
            }
          ].map((site, index) => (
            <Grow in={true} timeout={(index + 1) * 200} key={index}>
              <Box
                onMouseEnter={() => setHoveredCard(site.name)}
                onMouseLeave={() => setHoveredCard(null)}
                sx={{
                  background: 'rgba(255, 255, 255, 0.95)',
                  borderRadius: '15px',
                  padding: '2rem',
                  boxShadow: '0 10px 30px rgba(0, 0, 0, 0.1)',
                  transition: 'all 0.3s ease',
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: '1.5rem',
                  backdropFilter: 'blur(10px)',
                  '&:hover': {
                    transform: 'translateY(-5px)',
                    boxShadow: '0 20px 40px rgba(0, 0, 0, 0.15)'
                  }
                }}
              >
                <Box sx={{
                  background: site.color,
                  color: 'white',
                  width: '60px',
                  height: '60px',
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '1.5rem',
                  flexShrink: 0
                }}>
                  {site.icon}
                </Box>
                
                <Box sx={{ flex: 1 }}>
                  <Typography variant="h6" sx={{
                    fontWeight: 600,
                    color: '#2d3748',
                    marginBottom: '0.5rem'
                  }}>
                    {site.name}
                  </Typography>
                  <Typography sx={{
                    color: '#718096',
                    lineHeight: 1.6,
                    marginBottom: '1.5rem',
                    fontSize: '0.95rem'
                  }}>
                    {site.description}
                  </Typography>
                  <Box
                    component="a"
                    href={site.url}
                    target="_blank"
                    onClick={() => handleSiteClick(site.name, site.url)}
                    sx={{
                      background: 'linear-gradient(135deg, #1a2980, #26d0ce)',
                      color: 'white',
                      padding: '0.75rem 1.5rem',
                      borderRadius: '8px',
                      textDecoration: 'none',
                      fontWeight: 600,
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: '0.5rem',
                      transition: 'all 0.3s ease',
                      fontSize: '0.9rem',
                      '&:hover': {
                        transform: 'translateY(-2px)',
                        boxShadow: '0 5px 15px rgba(26, 41, 128, 0.3)'
                      }
                    }}
                  >
                    立即访问
                    <ExternalLinkIcon sx={{ fontSize: '0.8rem' }} />
                  </Box>
                </Box>
              </Box>
            </Grow>
          ))}
        </Box>

        {/* 页脚 */}
        <Box sx={{
          background: 'rgba(255, 255, 255, 0.9)',
          textAlign: 'center',
          padding: '1.5rem',
          color: '#718096',
          fontSize: '0.9rem',
          backdropFilter: 'blur(10px)',
          borderRadius: '10px'
        }}>
          <Typography>
            &copy; 2026 CCDTN. 版权所有
          </Typography>
        </Box>
      </Box>

      <style>
        {`
          @keyframes float {
            0%, 100% {
              transform: translate(0, 0) rotate(0deg);
            }
            33% {
              transform: translate(30px, -30px) rotate(120deg);
            }
            66% {
              transform: translate(-20px, 20px) rotate(240deg);
            }
          }
        `}
      </style>
    </Box>
  );
};

export const SecurePortal = () => {
  return (
    <Portal />
  );
};

export default SecurePortal;